import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import logo from '../assets/images/logo.png';
import heroBackground from '../assets/images/hero-background.png';
import { getAllPostsSorted } from '../data/blogPosts';
import { usePageSeo } from '../hooks/usePageSeo';

export default function BlogIndex() {
  const { user } = useAuth();
  const posts = getAllPostsSorted();

  usePageSeo({
    title: 'Blog — RDP Hosting Guides & Tips',
    description:
      'Expert guides on RDP hosting, remote desktop servers, USA and Netherlands locations, security, trading, developers, and choosing CPU, RAM, and disk. MyRDP blog.',
    keywords: [
      'RDP hosting blog',
      'remote desktop guides',
      'Windows RDP tips',
      'MyRDP articles',
      'cloud RDP help',
      'VPS RDP tutorials'
    ],
    canonicalPath: '/blog'
  });

  return (
    <div
      className="relative min-h-screen overflow-x-hidden bg-[#050613] text-white bg-no-repeat bg-contain bg-center-top"
      style={{ backgroundImage: `url(${heroBackground})` }}
    >
      <Header user={user} logo={logo} />

      <main className="px-4 pt-10 pb-32 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl">
          <div className="mx-auto max-w-4xl animate-fadeInSoft" style={{ animationDelay: '80ms' }}>
            <div className="mb-12 text-center">
              <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs tracking-widest text-white/80 backdrop-blur">
                + BLOG
              </span>
              <h1 className="mt-4 text-3xl font-medium text-white/90 sm:text-4xl md:text-5xl">
                RDP Hosting Insights
              </h1>
              <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-white/70 sm:text-base">
                Practical guides for <strong className="text-white/90">remote desktop servers</strong>,{' '}
                <strong className="text-white/90">Windows RDP VPS</strong>, security, locations, and specs—written
                for SEO teams, developers, and businesses using MyRDP.
              </p>
            </div>

            <div className="grid gap-6 sm:grid-cols-2">
              {posts.map((post, idx) => (
                <article
                  key={post.slug}
                  className="group flex flex-col rounded-2xl border border-[#EBEBEB1C] bg-[#FFFFFF14] p-6 backdrop-blur-sm transition-all duration-300 hover:border-[#486FFF]/35 hover:bg-[#FFFFFF18] hover:shadow-[0_0_40px_rgba(72,111,255,0.12)]"
                  style={{ animationDelay: `${120 + idx * 60}ms` }}
                >
                  <time
                    dateTime={post.datePublished}
                    className="text-xs font-medium uppercase tracking-wider text-[#486FFF]"
                  >
                    {new Date(post.datePublished + 'T12:00:00').toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}{' '}
                    · {post.readTime}
                  </time>
                  <h2 className="mt-3 text-xl font-semibold leading-snug text-white transition-colors group-hover:text-[#93b4ff] sm:text-2xl">
                    <Link to={`/blog/${post.slug}`} className="focus:outline-none focus-visible:ring-2 focus-visible:ring-[#486FFF] rounded-lg">
                      {post.title}
                    </Link>
                  </h2>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-white/75">{post.description}</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {post.keywords.slice(0, 4).map((kw) => (
                      <span
                        key={kw}
                        className="rounded-full border border-white/10 bg-[#050613]/40 px-2.5 py-0.5 text-[11px] text-white/55"
                      >
                        {kw}
                      </span>
                    ))}
                  </div>
                  <Link
                    to={`/blog/${post.slug}`}
                    className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-[#486FFF] hover:text-[#7c9cff]"
                  >
                    Read article
                    <span aria-hidden>→</span>
                  </Link>
                </article>
              ))}
            </div>
          </div>
        </div>
      </main>

      <Footer user={user} />
    </div>
  );
}
